import { RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react'

const NEAR_BOTTOM_THRESHOLD_PX = 32

interface UseAutoScrollToBottomOptions {
    containerRef: RefObject<HTMLElement | null>
    scrollSelector: string
    growthSelector: string
}

interface UseAutoScrollToBottomResult {
    isScrolledAway: boolean
    scrollToBottom: () => void
}

export const useAutoScrollToBottom = (
    { containerRef, scrollSelector, growthSelector }: UseAutoScrollToBottomOptions,
    dependency: unknown,
): UseAutoScrollToBottomResult => {
    const [isScrolledAway, setIsScrolledAway] = useState(false)
    const scrollToBottomImplRef = useRef<() => void>(() => {})

    const [previousDependency, setPreviousDependency] = useState(dependency)

    if (dependency !== previousDependency) {
        setPreviousDependency(dependency)
        setIsScrolledAway(false)
    }

    useLayoutEffect(() => {
        const container = containerRef.current

        if (! container) {
            return undefined
        }

        let isAtBottom = true
        let detach: (() => void) | undefined

        const attachTo = (scrollElement: HTMLElement, growthElement: HTMLElement): void => {
            const updateIsAtBottom = (): void => {
                const nowAtBottom = scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight
                    < NEAR_BOTTOM_THRESHOLD_PX

                if (nowAtBottom === isAtBottom) {
                    return
                }

                isAtBottom = nowAtBottom
                setIsScrolledAway(! nowAtBottom)
            }

            const chaseBottom = (): void => {
                if (isAtBottom) {
                    scrollElement.scrollTo({ top: scrollElement.scrollHeight })
                }
            }

            scrollToBottomImplRef.current = () => {
                scrollElement.scrollTo({ top: scrollElement.scrollHeight, behavior: 'smooth' })
            }

            chaseBottom()
            scrollElement.addEventListener('scroll', updateIsAtBottom, { passive: true })

            const resizeObserver = new ResizeObserver(chaseBottom)

            resizeObserver.observe(growthElement)

            detach = () => {
                scrollElement.removeEventListener('scroll', updateIsAtBottom)
                resizeObserver.disconnect()
                scrollToBottomImplRef.current = () => {}
            }
        }

        const tryAttach = (): boolean => {
            const scrollElement = container.querySelector<HTMLElement>(scrollSelector)
            const growthElement = container.querySelector<HTMLElement>(growthSelector)

            if (! scrollElement || ! growthElement) {
                return false
            }

            attachTo(scrollElement, growthElement)

            return true
        }

        if (tryAttach()) {
            return () => detach?.()
        }

        // The message list may still be loading and not in the DOM yet - watch for it to appear
        // instead of giving up on ever scrolling this conversation to the bottom.
        const mutationObserver = new MutationObserver(() => {
            if (tryAttach()) {
                mutationObserver.disconnect()
            }
        })

        mutationObserver.observe(container, { childList: true, subtree: true })

        return () => {
            mutationObserver.disconnect()
            detach?.()
        }
    }, [containerRef, scrollSelector, growthSelector, dependency])

    const scrollToBottom = useCallback((): void => {
        scrollToBottomImplRef.current()
    }, [])

    return { isScrolledAway, scrollToBottom }
}
